import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useUser } from "@clerk/react";
import { toast } from "sonner";

type Theme = "light" | "dark" | "system";

interface ThemeContextType {
  theme: Theme;
  pendingTheme: Theme | null;
  setTheme: (theme: Theme) => Promise<void>;
  toggleTheme: () => Promise<void>;
  isUpdatingTheme: boolean;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: React.ReactNode;
  defaultTheme?: Theme;
}

export function ThemeProvider({
  children,
  defaultTheme = "light",
}: ThemeProviderProps) {
  const { user } = useUser();
  const [theme, setThemeState] = useState<Theme>(defaultTheme);
  const [pendingTheme, setPendingTheme] = useState<Theme | null>(null);

  // Load theme from Clerk metadata on mount
  useEffect(() => {
    if (user?.unsafeMetadata?.theme) {
      setThemeState(user.unsafeMetadata.theme as Theme);
    }
  }, [user]);

  useEffect(() => {
    const root = window.document.documentElement;
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

    const applyTheme = () => {
      const isDark = theme === "dark" || (theme === "system" && mediaQuery.matches);
      root.classList.toggle("dark", isDark);
    };

    applyTheme();

    const handleSystemThemeChange = () => {
      if (theme === "system") {
        applyTheme();
      }
    };

    mediaQuery.addEventListener("change", handleSystemThemeChange);
    return () => mediaQuery.removeEventListener("change", handleSystemThemeChange);
  }, [theme]);

  const updateTheme = async (newTheme: Theme) => {
    if (newTheme === theme) return;
    setPendingTheme(newTheme);
    try {
      if (user) {
        await user.update({
          unsafeMetadata: {
            ...user.unsafeMetadata,
            theme: newTheme,
          },
        });
      }
      setThemeState(newTheme);
    } catch (error) {
      console.error("Failed to update theme in Clerk metadata:", error);
      toast.error("Failed to update theme");
    } finally {
      setPendingTheme(null);
    }
  };

  const isUpdatingTheme = pendingTheme !== null;

  const value = useMemo<ThemeContextType>(() => ({
    theme,
    pendingTheme,
    setTheme: updateTheme,
    toggleTheme: async () => {
      const isCurrentlyDark =
        theme === "dark" ||
        (theme === "system" && window.matchMedia("(prefers-color-scheme: dark)").matches);
      const newTheme: Theme = isCurrentlyDark ? "light" : "dark";
      await updateTheme(newTheme);
    },
    isUpdatingTheme,
  }), [theme, pendingTheme, user]);

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
}

